// Layouts are loaded via route `lazy` (and step wrappers imported from their own
// modules rather than the barrels) so the layout dependency trees stay out of the
// initial bundle.
import { PaidCallFlowStep } from 'components/PaidCallFlowStep/PaidCallFlowStep'
import { VideoCallFlowStep } from 'components/VideoCallFlowStep/VideoCallFlowStep'
import { LocationPaths, PaidVideoCallsPath, VideoCallsPath } from 'location.types'
import { Navigate, replace, RouteObject } from 'react-router'
import { getPaidVideoCallFlow } from 'screens/PaidVideoCalls/types'
import { getVideoCallFlow } from 'screens/VideoCalls/types'

export const buildVideoCallsFlowRoutes: (options: { isKeggFlow: boolean }) => RouteObject = ({
  isKeggFlow
}) => ({
  path: LocationPaths.VideoCalls,
  lazy: async () => ({
    Component: isKeggFlow
      ? (await import('screens/VideoCalls/Kegg/Layout')).KeggLayout
      : (await import('components/VideoCallFlowStep/Layout')).Layout
  }),
  children: [
    ...getVideoCallFlow().map((step, index) => ({
      index: index === 0,
      path: step.path,
      element: <VideoCallFlowStep isKeggFlow={isKeggFlow} />
    })),
    {
      path: 'integrate-stelo-account',
      loader: ({ request }) =>
        replace(
          `${LocationPaths.VideoCalls}/${VideoCallsPath.IntegrateDexcomAccount}${
            new URL(request.url).search
          }`
        )
    },
    // Old tabs can still point at retired steps. Redirect before the layout
    // mounts so its API requests run on the public flow entry URL.
    ...['welcome', 'post-health-goal', 'post-add-appointment-agenda'].map(
      (path): RouteObject => ({
        path,
        loader: ({ request }) =>
          replace(`${LocationPaths.VideoCalls}${new URL(request.url).search}`)
      })
    ),
    // The secondary-goal screen was merged into choose-primary-health-goal, but
    // this public URL may still exist in stale tabs, bookmarks, and sessions that
    // were mid-flow during the rollout.
    {
      path: VideoCallsPath.ChooseSecondaryHealthGoal,
      element: <Navigate to={LocationPaths.VideoCalls} replace />
    }
  ]
})

export const buildPaidVideoCallsFlowRoutes: (options: { isKeggFlow: boolean }) => RouteObject = ({
  isKeggFlow
}) => ({
  path: LocationPaths.PaidVideoCalls,
  lazy: async () => ({
    Component: isKeggFlow
      ? (await import('screens/VideoCalls/Kegg/PaidLayout')).KeggPaidLayout
      : (await import('components/PaidCallFlowStep/Layout')).Layout
  }),
  children: [
    ...getPaidVideoCallFlow().map((step, index) => ({
      index: index === 0,
      path: step.path,
      element: <PaidCallFlowStep stepIndex={index} />,
      handle: { index }
    })),
    {
      path: 'integrate-stelo-account',
      loader: ({ request }) =>
        replace(
          `${LocationPaths.PaidVideoCalls}/${PaidVideoCallsPath.IntegrateDexcomAccount}${
            new URL(request.url).search
          }`
        )
    }
  ]
})

export const privateVideoCallsRoutes: RouteObject = {
  path: LocationPaths.VideoCalls,
  lazy: async () => ({
    Component: (await import('components/PageLayout/PageLayout')).PageLayout
  }),
  children: [
    {
      path: VideoCallsPath.Dashboard,
      lazy: async () => ({ Component: (await import('screens/VideoCalls/Dashboard')).Dashboard })
    },
    {
      path: VideoCallsPath.ReviewAppointment,
      lazy: async () => ({
        Component: (await import('screens/VideoCalls/ReviewAppointment')).ReviewAppointment
      })
    },
    {
      path: VideoCallsPath.Chat,
      lazy: async () => ({ Component: (await import('screens/VideoCalls/Chat')).Chat })
    },
    {
      path: VideoCallsPath.DexcomCallback,
      lazy: async () => ({
        Component: (await import('screens/VideoCalls/DexcomCallback')).DexcomCallback
      })
    },
    {
      path: VideoCallsPath.WeightLossExperienceStart,
      lazy: async () => ({
        Component: (await import('screens/VideoCalls/WeightLossExperience/StartScreen'))
          .WeightLossExperienceStartScreen
      })
    },
    {
      path: VideoCallsPath.WeightLossExperienceCompleted,
      lazy: async () => ({
        Component: (await import('screens/VideoCalls/WeightLossExperience/CompletedScreen'))
          .WeightLossExperienceCompletedScreen
      })
    },
    {
      path: VideoCallsPath.WeightLossExperiencePlan,
      lazy: async () => ({
        Component: (await import('screens/VideoCalls/WeightLossExperience/PersonalPlan'))
          .WeightLossExperiencePersonalPlanContainer
      })
    }
  ]
}

export const stressManagement: RouteObject = {
  path: LocationPaths.VideoCalls,
  lazy: async () => ({
    Component: (await import('components/PageLayout/SecureDetailsLayout')).SecureDetailsLayout
  }),
  children: [
    {
      path: VideoCallsPath.StressManagementStart,
      lazy: async () => ({
        Component: (await import('screens/VideoCalls/StressManagement/StartScreen')).StartScreen
      })
    },
    {
      path: VideoCallsPath.StressManagementScheduleCall,
      lazy: async () => ({
        Component: (await import('screens/VideoCalls/StressManagement/ScheduleCallStep'))
          .ScheduleCallStep
      })
    },
    {
      path: VideoCallsPath.StressManagementCheckout,
      lazy: async () => ({
        Component: (await import('screens/VideoCalls/StressManagement/Checkout')).Checkout
      })
    },
    {
      path: VideoCallsPath.StressManagementUpsell,
      lazy: async () => ({
        Component: (await import('screens/VideoCalls/StressManagement/UpsellScreen')).UpsellScreen
      })
    }
  ]
}

export const signInRoute: RouteObject = {
  path: LocationPaths.VideoCalls,
  children: [
    {
      path: VideoCallsPath.SignIn,
      lazy: async () => ({ Component: (await import('screens/VideoCalls/SignIn')).SignIn })
    }
  ]
}
